import { Dialog, DialogTitle, Stack } from "@mui/material";
import IconButton from "../../../components/ui/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Dispatch, SetStateAction } from "react";
import { useIntl } from "../../../contexts/IntlContext";
import { YoutubeOwnerConnectionStatus } from "../../../services/youtubeOwnerConnection";
import SingleLiveCreationFlow from "./SingleLiveCreationFlow";
import LivePeriodCreationFlow from "./LivePeriod/LivePeriodCreationFlow";
import {
  CameraStatusRow,
  EstablishmentWithChannels,
  LivePlaylistOption,
} from "../types";

type Props = {
  open: boolean;
  onClose: () => void;
  canOperate: boolean;
  isAdmin: boolean;
  isOwnerOnly: boolean;
  isLocalhost: boolean;
  establishments: EstablishmentWithChannels[];
  setEstablishments: Dispatch<SetStateAction<EstablishmentWithChannels[]>>;
  establishmentsReady: boolean;
  selectedEstablishmentId: string;
  setSelectedEstablishmentId: (id: string) => void;
  selectedEstablishment: EstablishmentWithChannels | null;
  cameraCourts: CameraStatusRow[];
  templatesLoading: boolean;
  livePlaylists: LivePlaylistOption[];
  setLivePlaylists: Dispatch<SetStateAction<LivePlaylistOption[]>>;
  playlistListContextLiveId: number | null;
  youtubeConnectionStatus: YoutubeOwnerConnectionStatus | null;
  youtubeConnectionBlocked: boolean;
  onCreated?: () => void;
};

/**
 * Ponto de entrada único para criar uma Live. Quem decide o formulário é `canOperate`, não o
 * usuário: modo operacional (ADMIN/SUPPORT subindo a live agora, sem data) usa
 * `SingleLiveCreationFlow`; agendamento (só OWNER — ADMIN/SUPPORT sempre tem `canOperate=true`,
 * nunca chega aqui) usa `LivePeriodCreationFlow` — que já cobre 1 dia ou vários, com a mesma
 * diária de cobrança.
 */
export default function LiveCreationDialog({
  open,
  onClose,
  canOperate,
  isAdmin,
  isOwnerOnly,
  isLocalhost,
  establishments,
  setEstablishments,
  establishmentsReady,
  selectedEstablishmentId,
  setSelectedEstablishmentId,
  selectedEstablishment,
  cameraCourts,
  templatesLoading,
  livePlaylists,
  setLivePlaylists,
  playlistListContextLiveId,
  youtubeConnectionStatus,
  youtubeConnectionBlocked,
  onCreated,
}: Props) {
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <span>
          {intl.formatMessage({
            id: canOperate ? "liveStreams.newLive" : "liveStreams.period.title",
          })}
        </span>
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      {canOperate ? (
        <SingleLiveCreationFlow
          isAdmin={isAdmin}
          isLocalhost={isLocalhost}
          establishments={establishments}
          setEstablishments={setEstablishments}
          establishmentsReady={establishmentsReady}
          selectedEstablishmentId={selectedEstablishmentId}
          setSelectedEstablishmentId={setSelectedEstablishmentId}
          selectedEstablishment={selectedEstablishment}
          cameraCourts={cameraCourts}
          templatesLoading={templatesLoading}
          livePlaylists={livePlaylists}
          setLivePlaylists={setLivePlaylists}
          playlistListContextLiveId={playlistListContextLiveId}
        />
      ) : (
        <LivePeriodCreationFlow
          onClose={onClose}
          establishments={establishments}
          setEstablishments={setEstablishments}
          establishmentsReady={establishmentsReady}
          cameraOptions={cameraCourts}
          selectedEstablishmentId={selectedEstablishmentId}
          onEstablishmentChange={setSelectedEstablishmentId}
          templatesLoading={templatesLoading}
          livePlaylists={livePlaylists}
          setLivePlaylists={setLivePlaylists}
          playlistListContextLiveId={playlistListContextLiveId}
          isOwnerOnly={isOwnerOnly}
          isLocalhost={isLocalhost}
          youtubeConnectionStatus={youtubeConnectionStatus}
          youtubeConnectionBlocked={youtubeConnectionBlocked}
          onCreated={onCreated}
        />
      )}
    </Dialog>
  );
}
